/**
 * ============================================================================
 * CARE & COMFORT SUPPLEMENT  (staff / family completed — NOT resident-facing)
 * ============================================================================
 *
 * WHY THIS IS SEPARATE FROM THE INTERVIEW
 * ---------------------------------------
 * The DREAM Toolkit and Social History questionnaire collect several data
 * points that are valuable for care but are NOT appropriate to ask a resident
 * directly in a warm life-story interview — particularly trauma history,
 * fears, escalation triggers, and family addiction history. These tools are
 * designed to be completed by STAFF (using what they know and what family
 * shares), not by interviewing the resident point-blank.
 *
 * HIPAA HANDLING (assume covered; Colorado-based, multi-state)
 * ------------------------------------------------------------
 *   - LEGAL BASIS: Collecting and using this information by care staff for
 *     care planning is a "treatment / health-care-operations" (TPO) use, which
 *     HIPAA permits without a separate signed authorization. The care-comfort
 *     consent flag is therefore NOTICE + (where the resident lacks capacity) a
 *     personal-representative authorization for FAMILY involvement — it is not
 *     the legal basis for collection itself.
 *   - ACCESS: Gate every read/write behind staff role checks (community staff
 *     membership OR isSuperAdmin) on BOTH the Firestore rules and any Cloud
 *     Function, following the existing Promise.all([staffCheck, isSuperAdmin])
 *     pattern. Apply the minimum-necessary standard.
 *   - DISCLOSURE LIMITS: Fields/groups flagged `sensitive: true` are PHI that
 *     must be EXCLUDED, by default, from:
 *       (a) any AI prompt/summary (Gemini),
 *       (b) the public directory / community displays,
 *       (c) the family portal.
 *     `excludeFromAISummary`, `excludeFromPublic`, and `excludeFromFamily`
 *     make this data-driven so enforcement code can iterate the model rather
 *     than rely on a hand-maintained ID list.
 *   - HEIGHTENED CATEGORIES: trauma history and substance-use/addiction
 *     information are tagged `heightened: true`. Treat with the strictest
 *     handling (some states layer additional protection); collect only when
 *     known and necessary for care.
 *   - The transcripts themselves may contain PHI and are NOT de-identified by
 *     removing the resident's name. Sending them to Gemini requires the
 *     Generative AI usage to be covered by a Google Cloud BAA.
 *
 * These fields feed the DREAM bedtime routine and the care-facing Quiltt Card,
 * NOT the public "About" blurb or the family portal.
 * ============================================================================
 */

export const CARE_SUPPLEMENT = {
  id: 'careSupplement',
  title: 'Care & Comfort Supplement',
  description: 'Completed by staff with input from family — supports care planning and bedtime routines',
  completedBy: 'staff', // 'staff' | 'family'
  // Gates collection: requires careComfortConsent.granted (resident notice or
  // personal-representative authorization) recorded on the life story.
  requiresConsentScope: 'careComfortCollection',
  groups: [
    {
      id: 'comfort',
      title: 'What Brings Comfort',
      sensitive: false,
      fields: [
        {
          id: 'comfort_calming',
          prompt: "What helps this resident feel calm and reassured?",
          hint: "A cup of tea, a hug, certain music, a familiar phrase, a particular person",
          ecpFields: []
        },
        {
          id: 'comfort_sensory',
          prompt: "Are there sensory comforts that soothe them?",
          hint: "A favorite blanket or fabric, a scent like baking or aftershave, a familiar sound",
          ecpFields: []
        },
        {
          id: 'comfort_reassurance_words',
          prompt: "Words, phrases, or gestures that reassure them",
          hint: "From the DREAM/Social History sensory profile",
          ecpFields: []
        }
      ]
    },
    {
      id: 'sensory',
      title: 'Sensory Profile',
      sensitive: false,
      fields: [
        { id: 'sense_sight', prompt: "Sight — familiar or comforting things to see", hint: "Favorite colors, photos, view", ecpFields: [] },
        { id: 'sense_hearing', prompt: "Hearing — comforting sounds, and sounds to avoid", hint: "Favorite music; also any sounds that distress them", ecpFields: [] },
        { id: 'sense_taste', prompt: "Taste — comfort foods, treats, bedtime foods", hint: null, ecpFields: ['dining.comfortFoods'] },
        { id: 'sense_touch', prompt: "Touch — do they enjoy being hugged? Textures that bring fond memories?", hint: "Note hand-work history: sewing, woodworking, baking", ecpFields: [] },
        { id: 'sense_smell', prompt: "Smell — comforting aromas, and aromas to avoid", hint: null, ecpFields: [] }
      ]
    },
    {
      id: 'bedtime',
      title: 'Bedtime Environment (DREAM)',
      sensitive: false,
      fields: [
        { id: 'bed_light', prompt: "Preferred light at bedtime", hint: "Blinds closed, bedside lamp, nightlight, TV", ecpFields: ['sleep.lightNoise'] },
        { id: 'bed_noise', prompt: "Preferred sound at bedtime", hint: "Quiet, soft music, white noise, TV", ecpFields: ['sleep.lightNoise'] },
        { id: 'bed_temp', prompt: "Temperature preference", hint: "Warmer / cooler / no change", ecpFields: ['sleep.comfort'] },
        { id: 'bed_table', prompt: "What they want on the bedside table", hint: "Glasses, tissues, clock, phone, water", ecpFields: [] },
        { id: 'bed_door', prompt: "Door open or closed?", hint: null, ecpFields: [] },
        { id: 'bed_trouble', prompt: "What helps when they're having trouble sleeping?", hint: null, ecpFields: [] }
      ]
    },
    {
      id: 'wellbeing',
      title: 'Emotional Wellbeing & Triggers',
      sensitive: true, // PHI — excluded from AI summary, public + family views by default
      consentRequired: true,
      excludeFromAISummary: true,
      excludeFromPublic: true,
      excludeFromFamily: true,
      fields: [
        {
          id: 'trigger_upset',
          prompt: "Things that tend to upset, frustrate, or agitate them",
          hint: "From DREAM 'things that irritate or exacerbate.' Helps staff de-escalate.",
          sensitive: true,
          ecpFields: []
        },
        {
          id: 'trigger_fears',
          prompt: "Known fears",
          hint: "e.g., the dark, being alone, water, certain situations",
          sensitive: true,
          ecpFields: []
        },
        {
          id: 'trigger_trauma',
          prompt: "Traumatic experiences staff should be sensitive to (only if known and relevant to care)",
          hint: "Collect with care and only what's necessary. Per DREAM: trauma history can affect sleep.",
          sensitive: true,
          heightened: true, // trauma history — strictest handling
          ecpFields: []
        },
        {
          id: 'escalation_helpful',
          prompt: "Helpful approaches when they're distressed",
          hint: "Leave alone, offer a hug, certain reassuring phrases, redirect to a topic they love",
          sensitive: true,
          ecpFields: []
        },
        {
          id: 'addiction_history',
          prompt: "Family history of addiction / alcohol preferences & objections (if relevant to care)",
          hint: "From Social History. Informs whether alcoholic beverages are offered.",
          sensitive: true,
          heightened: true, // substance-use information — strictest handling
          ecpFields: ['dining.alcohol']
        }
      ]
    },
    {
      id: 'expression',
      title: 'Patterns of Expression',
      sensitive: true,
      consentRequired: true,
      excludeFromAISummary: true,
      excludeFromPublic: true,
      excludeFromFamily: true,
      fields: [
        {
          id: 'expression_patterns',
          prompt: "Expressive patterns to recognize",
          hint: "Wandering, repeated phrases, restlessness, seeking contact — and what they tend to mean",
          sensitive: true,
          ecpFields: []
        }
      ]
    }
  ]
};

/**
 * Derive the list of field IDs that must NEVER flow into AI summaries, the
 * public directory, or the family portal. Derived from the model so it cannot
 * drift from the `sensitive` flags above.
 */
export function getSensitiveCareFieldIds() {
  const ids = [];
  for (const group of CARE_SUPPLEMENT.groups) {
    for (const field of group.fields) {
      if (field.sensitive || group.sensitive) ids.push(field.id);
    }
  }
  return ids;
}

/**
 * Static snapshot of the sensitive field IDs (computed at module load).
 * The Cloud Functions side keeps its own mirror in functions/careSupplement.js
 * because src/ and functions/ are separate deploy units that cannot share
 * imports — keep the two lists in sync.
 */
export const SENSITIVE_FIELD_IDS = getSensitiveCareFieldIds();
